import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Activity,
  Globe,
  TrendingUp,
  UserX,
  ArrowRight,
  RefreshCw,
  Bot,
  Package,
  MessageSquare,
} from 'lucide-react';
import {
  getAdminStats,
  getUserGrowthChart,
  getActivityChart,
  getAdminFunnel,
} from '../../services/adminService';
import type { AdminStats, ChartDataPoint, FunnelData } from '../../types/admin';
import { logger } from '../../lib/logger';

// Simple line chart component
function SimpleLineChart({
  data,
  dataKey,
  color,
  height = 120,
}: {
  data: ChartDataPoint[];
  dataKey: 'count' | 'page_views' | 'form_submissions';
  color: string;
  height?: number;
}) {
  if (!data || data.length === 0) return null;

  const values = data.map((d) => (d[dataKey] as number) || 0);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = max - min;

  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * 100;
      const y = 100 - ((value - min) / range) * 100;
      return `${x},${y}`;
    })
    .join(' ');

  // Create area fill path
  const areaPath = `M0,100 L${points
    .split(' ')
    .map((p, i) => {
      const [x, y] = p.split(',');
      return i === 0 ? `${x},${y}` : `L${x},${y}`;
    })
    .join(' ')} L100,100 Z`;

  return (
    <div style={{ height }} className="w-full">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id={`gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#gradient-${color})`} />
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          points={points}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

// Funnel visualization
function FunnelChart({ data }: { data: FunnelData }) {
  const steps = [
    { label: 'Registriert', value: data.registered, pct: 100 },
    { label: 'Profil vollständig', value: data.profile_complete, pct: data.profile_complete_pct },
    { label: 'Seite veröffentlicht', value: data.page_published, pct: data.page_published_pct },
  ];

  return (
    <div className="space-y-3">
      {steps.map((step, index) => (
        <div key={step.label}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-slate-400">{step.label}</span>
            <span className="text-sm font-medium text-white">
              {step.value} <span className="text-slate-500">({step.pct}%)</span>
            </span>
          </div>
          <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${step.pct}%`,
                background:
                  index === 0
                    ? 'linear-gradient(90deg, #f97316, #fb923c)'
                    : index === 1
                    ? 'linear-gradient(90deg, #8b5cf6, #a78bfa)'
                    : 'linear-gradient(90deg, #22c55e, #4ade80)',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdminDashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [userGrowth, setUserGrowth] = useState<ChartDataPoint[]>([]);
  const [activity, setActivity] = useState<ChartDataPoint[]>([]);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    try {
      const [statsData, growthData, activityData, funnelData] = await Promise.all([
        getAdminStats(),
        getUserGrowthChart(30),
        getActivityChart(30),
        getAdminFunnel(),
      ]);
      setStats(statsData);
      setUserGrowth(growthData);
      setActivity(activityData);
      setFunnel(funnelData);
    } catch (error) {
      logger.error('Error loading admin data:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-slate-400 mt-1">Plattform-Statistiken und Übersicht</p>
        </div>
        <button
          onClick={() => loadData(true)}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Gesamt Nutzer</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-xs font-medium text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full">
              7 Tage
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.new_users_7d || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Neue Nutzer</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center">
              <Activity className="w-5 h-5 text-violet-400" />
            </div>
            <span className="text-xs font-medium text-violet-400 bg-violet-400/10 px-2 py-1 rounded-full">
              7 Tage
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.active_users_7d || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Aktive Nutzer</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-orange-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.published_pages || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Veröffentlichte Seiten</p>
        </div>
      </div>

      {/* Second Row KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.total_leads || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Gesamt Leads</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-xs font-medium text-amber-400 bg-amber-400/10 px-2 py-1 rounded-full">
              30 Tage
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.new_users_30d || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Neue Nutzer</p>
        </div>

        <Link
          to="/admin/ai"
          className="bg-slate-900 border border-slate-800 rounded-2xl p-5 hover:bg-slate-800/50 transition-colors group"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center">
              <Bot className="w-5 h-5 text-violet-400" />
            </div>
            <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-violet-400 transition-colors" />
          </div>
          <p className="text-lg font-semibold text-white">AI Agent</p>
          <p className="text-sm text-slate-400 mt-1">KI-Statistiken anzeigen</p>
        </Link>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
              <UserX className="w-5 h-5 text-red-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.suspended_users || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Gesperrte Nutzer</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-white">Nutzer-Wachstum</h2>
              <p className="text-sm text-slate-400">Letzte 30 Tage</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              Neue Registrierungen
            </div>
          </div>
          <SimpleLineChart data={userGrowth} dataKey="count" color="#f97316" height={160} />
          <div className="flex justify-between mt-4 text-xs text-slate-500">
            <span>Vor 30 Tagen</span>
            <span>Heute</span>
          </div>
        </div>

        {/* Activity Chart */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-white">Aktivität</h2>
              <p className="text-sm text-slate-400">Letzte 30 Tage</p>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-violet-500" />
                Page Views
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                Submissions
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <SimpleLineChart data={activity} dataKey="page_views" color="#8b5cf6" height={70} />
            <SimpleLineChart
              data={activity}
              dataKey="form_submissions"
              color="#22c55e"
              height={70}
            />
          </div>
          <div className="flex justify-between mt-4 text-xs text-slate-500">
            <span>Vor 30 Tagen</span>
            <span>Heute</span>
          </div>
        </div>
      </div>

      {/* Funnel and Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Funnel */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-white">Nutzer-Funnel</h2>
            <p className="text-sm text-slate-400">Conversion durch Setup-Steps</p>
          </div>
          {funnel && <FunnelChart data={funnel} />}
        </div>

        {/* Quick Actions */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-white">Schnellzugriff</h2>
            <p className="text-sm text-slate-400">Häufige Admin-Aktionen</p>
          </div>
          <div className="space-y-3">
            <Link
              to="/admin/users"
              className="flex items-center justify-between p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">Nutzerverwaltung</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
            </Link>

            <Link
              to="/admin/users?status=suspended"
              className="flex items-center justify-between p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <UserX className="w-5 h-5 text-red-400" />
                <span className="text-white font-medium">Gesperrte Nutzer</span>
                {(stats?.suspended_users || 0) > 0 && (
                  <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
                    {stats?.suspended_users}
                  </span>
                )}
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
            </Link>

            <Link
              to="/admin/products"
              className="flex items-center justify-between p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-orange-400" />
                <span className="text-white font-medium">Produktkatalog verwalten</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
            </Link>

            <Link
              to="/admin/feedback"
              className="flex items-center justify-between p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-yellow-400" />
                <span className="text-white font-medium">Nutzer-Feedback anzeigen</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
